/**
 * Review and submit observation plans dialog.
 * Includes baseline controls, altitude constraints, altitude profiles, and the plan table.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  TextField,
  Slider,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { formatTime, formatDuration, formatLocalTime } from '../utils/timeFormatting';
import AltitudeProfileSection from './AltitudeProfileSection';
import DarkTimeWarningAlert from '../../../components/observations/DarkTimeWarningAlert';
import StorageDestinationPicker from '../../../components/storage/StorageDestinationPicker';
import { renderProjectOption, projectValueLabel } from './projectOptionLabel';

const BaselineControls = ({
  baselineBefore,
  baselineAfter,
  setBaselineBefore,
  setBaselineAfter,
  onApply,
}) => (
  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
    <Typography variant="subtitle2" gutterBottom>
      Baseline Observation Times (Current: {baselineBefore} min before, {baselineAfter} min after)
    </Typography>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={5}>
        <TextField
          label="Before Transit (minutes)"
          type="number"
          size="small"
          fullWidth
          value={baselineBefore}
          onChange={(e) => {
            const newValue = Math.max(0, parseInt(e.target.value) || 0);
            setBaselineBefore(newValue);
          }}
          inputProps={{ min: 0, max: 180, step: 5 }}
          helperText="Baseline before ingress"
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <TextField
          label="After Transit (minutes)"
          type="number"
          size="small"
          fullWidth
          value={baselineAfter}
          onChange={(e) => {
            const newValue = Math.max(0, parseInt(e.target.value) || 0);
            setBaselineAfter(newValue);
          }}
          inputProps={{ min: 0, max: 180, step: 5 }}
          helperText="Baseline after egress"
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Button variant="outlined" size="small" fullWidth onClick={onApply}>
          Apply
        </Button>
      </Grid>
    </Grid>
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
      Baseline observations are used for accurate photometry. Reducing baseline times can help avoid conflicts with other observations.
    </Typography>
  </Box>
);

const AltitudeConstraintControl = ({
  minAltitude,
  setMinAltitude,
  selectedObservatoryId,
  selectedTransits,
  transits,
  observatories,
  baselineBefore,
  baselineAfter,
}) => (
  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
    <Typography variant="subtitle2" gutterBottom>
      Minimum Altitude Constraint: {minAltitude}
    </Typography>
    <Slider
      value={minAltitude}
      onChange={(e, newValue) => setMinAltitude(newValue)}
      min={5}
      max={60}
      step={1}
      marks={[
        { value: 5, label: '5' },
        { value: 20, label: '20' },
        { value: 30, label: '30' },
        { value: 40, label: '40' },
        { value: 60, label: '60' }
      ]}
      valueLabelDisplay="auto"
      sx={{ mt: 2, mb: 1 }}
    />
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      Minimum altitude above horizon for target observations. Lower altitudes have higher airmass and atmospheric extinction.
      Recommended: 30 for most observations, 20 for critical time-sensitive events, 5 for horizon observations.
    </Typography>

    <AltitudeProfileSection
      selectedObservatoryId={selectedObservatoryId}
      selectedTransits={selectedTransits}
      transits={transits}
      observatories={observatories}
      baselineBefore={baselineBefore}
      baselineAfter={baselineAfter}
      minAltitude={minAltitude}
    />
  </Box>
);

const PlanTable = ({
  observationPlans,
  setObservationPlans,
  telescopeConfig,
  observatoryLocation,
}) => (
  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          <TableCell>Target</TableCell>
          <TableCell align="center">Start (UTC)</TableCell>
          <TableCell align="center">End (UTC)</TableCell>
          <TableCell align="center">Filter</TableCell>
          <TableCell align="center">Mode</TableCell>
          <TableCell align="center">Exp Time (s)</TableCell>
          <TableCell align="center">Binning</TableCell>
          <TableCell align="center">Priority</TableCell>
          <TableCell align="center">Duration</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {observationPlans.map((plan, index) => (
          <PlanRow
            key={index}
            plan={plan}
            index={index}
            observationPlans={observationPlans}
            setObservationPlans={setObservationPlans}
            telescopeConfig={telescopeConfig}
            observatoryLocation={observatoryLocation}
          />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const PlanRow = ({ plan, index, observationPlans, setObservationPlans, telescopeConfig, observatoryLocation }) => {
  const updatePlan = (field, value) => {
    const newPlans = [...observationPlans];
    if (field.startsWith('exposure_plan.')) {
      const key = field.split('.')[1];
      if (!newPlans[index].exposure_plan) newPlans[index].exposure_plan = {};
      newPlans[index].exposure_plan[key] = value;
    } else {
      newPlans[index][field] = value;
    }
    setObservationPlans(newPlans);
  };

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" fontWeight="bold">
          {plan.target?.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          V={plan.target?.vmag?.toFixed(1)}, Depth={plan.expected_metrics?.transit_depth?.toFixed(2)}%
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight="medium">
          {plan.observationStartTime ? formatTime(plan.observationStartTime) : 'N/A'}
        </Typography>
        {observatoryLocation?.timezone && plan.observationStartTime && (
          <Typography variant="caption" color="text.secondary" display="block">
            {formatLocalTime(plan.observationStartTime, observatoryLocation.timezone)}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight="medium">
          {plan.observationEndTime ? formatTime(plan.observationEndTime) : 'N/A'}
        </Typography>
        {observatoryLocation?.timezone && plan.observationEndTime && (
          <Typography variant="caption" color="text.secondary" display="block">
            {formatLocalTime(plan.observationEndTime, observatoryLocation.timezone)}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <Select
          size="small"
          value={plan.exposure_plan?.filter || telescopeConfig.filters?.[0] || 'L'}
          onChange={(e) => updatePlan('exposure_plan.filter', e.target.value)}
          sx={{ minWidth: 80 }}
        >
          {(telescopeConfig.filters && telescopeConfig.filters.length > 0
            ? telescopeConfig.filters
            : ['L', 'R', 'G', 'B', 'V', 'Clear']
          ).map(filter => (
            <MenuItem key={filter} value={filter}>{filter}</MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Chip label="Fixed Time" size="small" color="secondary" variant="outlined" />
      </TableCell>
      <TableCell align="center">
        <TextField
          size="small"
          type="number"
          value={plan.exposure_plan?.exposure_time || 120}
          onChange={(e) => updatePlan('exposure_plan.exposure_time', parseInt(e.target.value))}
          inputProps={{ min: 1, max: 1800, step: 1 }}
          sx={{ width: 80 }}
        />
      </TableCell>
      <TableCell align="center">
        <Select
          size="small"
          value={plan.exposure_plan?.binning || 1}
          onChange={(e) => updatePlan('exposure_plan.binning', parseInt(e.target.value))}
          sx={{ width: 80 }}
        >
          <MenuItem value={1}>1x1</MenuItem>
          <MenuItem value={2}>2x2</MenuItem>
          <MenuItem value={3}>3x3</MenuItem>
          <MenuItem value={4}>4x4</MenuItem>
        </Select>
      </TableCell>
      <TableCell align="center">
        <Select
          size="small"
          value={plan.priority || 5}
          onChange={(e) => updatePlan('priority', parseInt(e.target.value))}
          sx={{ width: 80 }}
        >
          <MenuItem value={1}>1 (Low)</MenuItem>
          <MenuItem value={3}>3</MenuItem>
          <MenuItem value={5}>5 (Med)</MenuItem>
          <MenuItem value={7}>7</MenuItem>
          <MenuItem value={9}>9 (High)</MenuItem>
          <MenuItem value={10}>10 (Max)</MenuItem>
        </Select>
      </TableCell>
      <TableCell align="center">
        {formatDuration(plan.totalDurationHours || plan.expected_metrics?.transit_duration_hours || 0)}
      </TableCell>
    </TableRow>
  );
};

const ObservationReviewDialog = ({
  open,
  onClose,
  observationPlans,
  setObservationPlans,
  baselineBefore,
  baselineAfter,
  setBaselineBefore,
  setBaselineAfter,
  minAltitude,
  setMinAltitude,
  selectedObservatoryId,
  observatories,
  handleObservatoryChange,
  projects,
  selectedProjectId,
  setSelectedProjectId,
  storage,                    // #1474: useStorageDestinations() for the selected project
  telescopeConfig,
  observatoryLocation,
  selectedTransits,
  setSelectedTransits,
  transits,
  submitting,
  submitError,
  setSubmitError,
  darkTimeWarnings = [],      // #1119: per-transit "window runs past dark time" advisories
  onDismissDarkTimeWarnings,  // acknowledges them and closes out the batch
  onSubmit,
  onRegenerate,
}) => {
  const handleDiscard = () => {
    setObservationPlans([]);
    onClose();
    setSelectedTransits(new Set());
    setSubmitError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Review Observation Plans</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review and adjust the generated observation plans before submitting them to the scheduler.
          These are fixed-time observations that will continuously capture exposures during the specified time window.
          Adjust baseline times to optimize scheduling and avoid conflicts.
        </Typography>

        <BaselineControls
          baselineBefore={baselineBefore}
          baselineAfter={baselineAfter}
          setBaselineBefore={setBaselineBefore}
          setBaselineAfter={setBaselineAfter}
          onApply={() => {
            console.log('Apply button clicked, current baselines:', { baselineBefore, baselineAfter });
            alert(`Current baselines: ${baselineBefore} min before, ${baselineAfter} min after`);
            onRegenerate();
          }}
        />

        <AltitudeConstraintControl
          minAltitude={minAltitude}
          setMinAltitude={setMinAltitude}
          selectedObservatoryId={selectedObservatoryId}
          selectedTransits={selectedTransits}
          transits={transits}
          observatories={observatories}
          baselineBefore={baselineBefore}
          baselineAfter={baselineAfter}
        />

        {/* Observatory and Project Selection */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Observatory</InputLabel>
              <Select
                value={selectedObservatoryId || ''}
                onChange={(e) => handleObservatoryChange(e.target.value)}
                label="Observatory"
              >
                {observatories.map((observatory) => (
                  <MenuItem key={observatory._id} value={observatory._id}>
                    {observatory.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {telescopeConfig.filters && telescopeConfig.filters.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Available filters: {telescopeConfig.filters.join(', ')}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                label="Project"
                renderValue={(val) => projectValueLabel(projects.find((p) => p._id === val))}
              >
                {projects
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {renderProjectOption(project)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {storage && (
          <Box sx={{ mb: 2 }}>
            <StorageDestinationPicker storage={storage} />
          </Box>
        )}

        <PlanTable
          observationPlans={observationPlans}
          setObservationPlans={setObservationPlans}
          telescopeConfig={telescopeConfig}
          observatoryLocation={observatoryLocation}
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {observationPlans.length} observation{observationPlans.length > 1 ? 's' : ''} will be created.
            Total observation time: {formatDuration(observationPlans.reduce((sum, p) => {
              const transitDuration = p.expected_metrics?.transit_duration_hours || 0;
              return sum + transitDuration + (baselineBefore / 60) + (baselineAfter / 60);
            }, 0))}
          </Typography>
        </Alert>

        {submitError && (
          <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/*
          #1119: transit windows come from ephemerides and routinely cross twilight. The
          observations WERE created; what this reports is that the dispatcher will stop each listed
          run at dark time, so the tail of its window goes unobserved. Listed per target, because a
          batch where three of eight were quietly truncated is the failure worth seeing.
        */}
        <DarkTimeWarningAlert
          messages={darkTimeWarnings}
          onContinue={onDismissDarkTimeWarnings}
          continueLabel="Got it"
          sx={{ mt: 2, mb: 0 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleDiscard}>Discard Plans</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
        >
          {submitting ? 'Submitting...' : 'Submit Observations'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObservationReviewDialog;
